import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { AnswerCapsule } from '@/components/marketing/answer-capsule';
import { Btn, Eyebrow, H2 } from '@/components/marketing/warm/primitives';
import { setDocumentMeta, setCanonical, injectJsonLdList, buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema } from '@/lib/seo';
import { Mockup, Thumb, Chip, Window } from './mockups';
import pagesJson from './pages.json';

type Row = { title: string; body: string; bullets: string[]; mockup: string; mockupData?: Record<string, unknown> };
type TeamCard = { team: string; categories: string[]; who: string; extras: string };
export type V2Page = {
  url: string; title: string; meta: string; eyebrow: string; h1: string; lead: string;
  hero: { mockup: string; mockupData?: Record<string, unknown>; ctaPrimary: string; ctaSecondary: { label: string; href: string } };
  answer: string; rows: Row[]; steps: { title: string; body: string; thumb: string }[];
  facts: { value: string; label: string }[];
  module: { type: string; title: string; note?: string; teams?: TeamCard[]; cols?: string[]; rows?: string[][]; tabs?: { name: string; items: string[] }[] };
  cost: { title: string; label: string; lines: { label: string; value: string }[]; total: string; note?: string; comparison?: { label: string; value: string; source: string; checked: string } };
  setup: { title: string; label: string; categories: string[]; sla: { priority: string; firstResponse: string; resolution: string }[]; roles: string[]; note?: string };
  notYet: string[]; faq: { q: string; a: string }[]; related: { href: string; title: string; line: string }[];
  cta: { title: string; subtitle: string };
};

export const V2_PAGES = pagesJson as unknown as V2Page[];
const ink = { color: 'var(--warm-text)' };
const muted = { color: 'var(--warm-muted)' };
const card = { background: 'var(--warm-card)', border: '1px solid var(--warm-border)' };

/** Section wrapper: always visible (no scroll-reveal, nothing depends on JS), calm vertical rhythm. */
function RevealSection({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <section style={style}>
      <div className="mx-auto max-w-[1200px] px-5 py-14 sm:px-8 md:py-20">{children}</div>
    </section>
  );
}

function Hero({ p }: { p: V2Page }) {
  // No scroll-reveal and no JS dependency above the fold: plain markup, always visible.
  return (
    <section className="pt-28 pb-10 md:pt-32 md:pb-14">
      <div className="mx-auto grid max-w-[1200px] items-center gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-14">
        <div className="min-w-0 lg:col-span-6">
          <p className="warm-eyebrow mb-5">{p.eyebrow}</p>
          <h1 className="font-display text-[2.5rem] font-light leading-[1.05] tracking-tight md:text-[3.1rem]" style={ink}>{p.h1}</h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed" style={muted}>{p.lead}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Btn to="/auth">{p.hero.ctaPrimary} <ArrowRight className="size-3.5" /></Btn>
            <Link to={p.hero.ctaSecondary.href} className="inline-flex min-h-[44px] items-center px-3 text-sm" style={muted}>{p.hero.ctaSecondary.label}</Link>
          </div>
        </div>
        <div className="min-w-0 lg:col-span-6"><Mockup id={p.hero.mockup} data={p.hero.mockupData} /></div>
      </div>
    </section>
  );
}

function FeatureRows({ rows }: { rows: Row[] }) {
  return (
    <RevealSection>
      <div className="space-y-12 md:space-y-16">
        {rows.map((r, i) => (
          <div key={r.title} className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
            <div className={`min-w-0 ${i % 2 ? 'lg:order-2' : ''}`}>
              <h2 className="font-display text-[1.75rem] font-light leading-tight md:text-[2.25rem]" style={ink}>{r.title}</h2>
              <p className="mt-3 text-base leading-relaxed" style={muted}>{r.body}</p>
              <ul className="mt-5 space-y-2">
                {r.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm" style={ink}>
                    <IconCheck size={16} stroke={2.25} className="mt-0.5 shrink-0" style={{ color: 'var(--warm-ok)' }} />{b}
                  </li>
                ))}
              </ul>
            </div>
            <div className={`min-w-0 ${i % 2 ? 'lg:order-1' : ''}`}><Mockup id={r.mockup} data={r.mockupData} /></div>
          </div>
        ))}
      </div>
    </RevealSection>
  );
}

function Steps({ steps }: { steps: V2Page['steps'] }) {
  return (
    <RevealSection style={{ background: 'var(--warm-surface)' }}>
      <Eyebrow>How it works</Eyebrow>
      <ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <li key={s.title} className="relative rounded-2xl p-4" style={card}>
            <Thumb id={s.thumb} />
            <p className="mt-4 flex items-center gap-2 text-sm font-medium" style={ink}>
              <span className="flex size-6 items-center justify-center rounded-full text-xs" style={{ background: 'var(--warm-inset)' }}>{i + 1}</span>{s.title}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed" style={muted}>{s.body}</p>
          </li>
        ))}
      </ol>
    </RevealSection>
  );
}

function Facts({ facts }: { facts: V2Page['facts'] }) {
  return (
    <section style={{ borderTop: '1px solid var(--warm-border)', borderBottom: '1px solid var(--warm-border)' }}>
      <dl className="mx-auto grid max-w-[1200px] grid-cols-2 gap-6 px-5 py-8 sm:px-8 md:grid-cols-4">
        {facts.map((f) => (
          <div key={f.label}>
            <dt className="font-display text-3xl font-light md:text-4xl" style={ink}>{f.value}</dt>
            <dd className="mt-1 text-sm" style={muted}>{f.label}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Module({ m }: { m: V2Page['module'] }) {
  const head = (
    <>
      <Eyebrow>{m.type === 'teamsGrid' ? 'Teams in one workspace' : m.type === 'table' ? 'In detail' : m.type === 'code' ? 'For developers' : 'Try it'}</Eyebrow>
      <H2>{m.title}</H2>
      {m.note && <p className="mt-2 text-sm" style={muted}>{m.note}</p>}
    </>
  );
  if (m.type === 'teamsGrid' && m.teams) {
    return (
      <RevealSection>
        {head}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {m.teams.map((t, i, all) => (
            <div key={t.team} className={`rounded-2xl p-5 ${all.length === 5 ? (i < 3 ? 'lg:col-span-2' : 'lg:col-span-3') : 'lg:col-span-2'} ${all.length === 5 && i === 4 ? 'sm:col-span-2' : ''}`} style={card}>
              <h3 className="text-base font-medium" style={ink}>{t.team}</h3>
              <div className="mt-3 flex flex-wrap gap-1.5">{t.categories.map((c) => <Chip key={c}>{c}</Chip>)}</div>
              <p className="mt-3 text-sm" style={muted}><span style={ink}>Who: </span>{t.who}</p>
              <p className="mt-1 text-sm" style={muted}><span style={ink}>Also uses: </span>{t.extras}</p>
            </div>
          ))}
        </div>
      </RevealSection>
    );
  }
  if (m.type === 'table' && m.cols && m.rows) {
    return (
      <RevealSection>
        {head}
        <div className="mt-8 overflow-x-auto rounded-2xl" style={card}>
          <table className="w-full min-w-[480px] text-left text-sm">
            <thead><tr style={{ borderBottom: '1px solid var(--warm-border)', ...muted }}>{m.cols.map((c) => <th key={c} className="px-5 py-3 font-medium">{c}</th>)}</tr></thead>
            <tbody>
              {m.rows.map((r) => (
                <tr key={r[0]} style={{ borderTop: '1px solid var(--warm-border)' }}>
                  {r.map((c, i) => <td key={i} className="px-5 py-3 align-top" style={i === 0 ? { ...ink, fontWeight: 500 } : muted}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </RevealSection>
    );
  }
  if ((m.type === 'tabs' || m.type === 'code') && m.tabs) {
    // Every tab is rendered as its own panel, so all content is in the page without scripts.
    const mono = m.type === 'code';
    return (
      <RevealSection>
        {head}
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {m.tabs.map((t) => (
            <div key={t.name} className="rounded-2xl p-5" style={card}>
              <h3 className="text-sm font-medium" style={ink}>{t.name}</h3>
              <ul className="mt-3 space-y-2">
                {t.items.map((i) => (
                  <li key={i} className={mono ? 'rounded-lg px-2.5 py-1.5 text-[12.5px]' : 'text-sm'} style={mono ? { background: 'var(--warm-page)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } : muted}>{i}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </RevealSection>
    );
  }
  return null;
}

function Cost({ c }: { c: V2Page['cost'] }) {
  return (
    <RevealSection style={{ background: 'var(--warm-surface)' }}>
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Eyebrow>{c.title}</Eyebrow>
          <H2>{c.total}</H2>
          {c.note && <p className="mt-3 text-base" style={muted}>{c.note}</p>}
          <p className="mt-4 inline-flex rounded-md px-2 py-1 text-xs font-medium" style={{ background: 'var(--chip-amber-bg)', color: 'var(--chip-amber)' }}>{c.label}</p>
        </div>
        <div className="lg:col-span-7">
          <Window title="Cost" label={c.label}>
            <dl className="divide-y" style={{ borderColor: 'var(--warm-border)' }}>
              {c.lines.map((l) => (
                <div key={l.label} className="flex items-baseline justify-between gap-4 py-3" style={{ borderColor: 'var(--warm-border)' }}>
                  <dt className="text-sm" style={muted}>{l.label}</dt>
                  <dd className="text-sm font-medium tabular-nums" style={ink}>{l.value}</dd>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-4 py-3" style={{ borderColor: 'var(--warm-border)' }}>
                <dt className="text-sm font-medium" style={ink}>Total</dt>
                <dd className="font-display text-2xl" style={ink}>{c.total}</dd>
              </div>
            </dl>
            {c.comparison && (
              <p className="mt-3 rounded-xl p-3 text-sm" style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)', ...muted }}>
                For comparison: {c.comparison.label} is <strong style={ink}>{c.comparison.value}</strong> ({c.comparison.source}, checked {c.comparison.checked}).
              </p>
            )}
          </Window>
        </div>
      </div>
    </RevealSection>
  );
}

function Setup({ s }: { s: V2Page['setup'] }) {
  return (
    <RevealSection>
      <Eyebrow>{s.title}</Eyebrow>
      <p className="mb-6 inline-flex rounded-md px-2 py-1 text-xs font-medium" style={{ background: 'var(--warm-inset)', ...muted }}>{s.label}</p>
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="rounded-2xl p-5 lg:col-span-4" style={card}>
          <h3 className="text-sm font-medium" style={ink}>Categories</h3>
          <div className="mt-3 flex flex-wrap gap-1.5">{s.categories.map((c) => <Chip key={c}>{c}</Chip>)}</div>
          <h3 className="mt-6 text-sm font-medium" style={ink}>Roles</h3>
          <ul className="mt-2 space-y-1.5 text-sm" style={muted}>{s.roles.map((r) => <li key={r}>{r}</li>)}</ul>
        </div>
        {s.sla.length > 0 && <div className="overflow-x-auto rounded-2xl p-5 lg:col-span-8" style={card}>
          <h3 className="text-sm font-medium" style={ink}>SLA targets</h3>
          <table className="mt-3 w-full min-w-[420px] text-left text-sm">
            <thead><tr style={muted}><th className="pb-2 font-medium">Priority</th><th className="pb-2 font-medium">First response</th><th className="pb-2 font-medium">Resolution</th></tr></thead>
            <tbody>
              {s.sla.map((r) => (
                <tr key={r.priority} style={{ borderTop: '1px solid var(--warm-border)', ...ink }}>
                  <td className="py-2.5"><span className="inline-flex items-center gap-2"><span className="size-2 rounded-full" style={{ background: { Critical: '#C2410C', High: '#D97706', Medium: '#2563EB', Low: '#6B7280' }[r.priority] }} />{r.priority}</span></td>
                  <td className="py-2.5">{r.firstResponse}</td><td className="py-2.5">{r.resolution}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {s.note && <p className="mt-4 text-xs leading-relaxed" style={muted}>{s.note}</p>}
        </div>}
        {s.sla.length === 0 && s.note && <p className="self-center text-sm leading-relaxed lg:col-span-8" style={muted}>{s.note}</p>}
      </div>
    </RevealSection>
  );
}

function NotYet({ items }: { items: string[] }) {
  return (
    <RevealSection style={{ background: 'var(--warm-surface)' }}>
      <div className="max-w-3xl rounded-2xl p-6" style={card}>
        <h2 className="font-display text-2xl font-light" style={ink}>What Kora does not do yet</h2>
        <ul className="mt-4 space-y-2 text-sm" style={muted}>
          {items.map((i) => <li key={i} className="flex gap-2.5"><span className="mt-2 size-1 shrink-0 rounded-full" style={{ background: 'var(--warm-muted)' }} />{i}</li>)}
        </ul>
      </div>
    </RevealSection>
  );
}

function Faq({ faq }: { faq: V2Page['faq'] }) {
  // Native <details>: every answer is in the DOM and readable without JS. First item open.
  return (
    <RevealSection>
      <Eyebrow>Common questions</Eyebrow>
      <div className="mt-4 grid gap-x-10 md:grid-cols-2">
        {faq.map((f, i) => (
          <details key={f.q} open={i === 0} className="group" style={{ borderBottom: '1px solid var(--warm-border)' }}>
            <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-4 py-3 text-[15px] font-medium" style={ink}>
              {f.q}<IconPlus size={18} stroke={1.5} className="shrink-0 transition-transform group-open:rotate-45" style={muted} />
            </summary>
            <p className="pb-4 text-sm leading-relaxed" style={muted}>{f.a}</p>
          </details>
        ))}
      </div>
    </RevealSection>
  );
}

function Related({ items }: { items: V2Page['related'] }) {
  return (
    <RevealSection style={{ background: 'var(--warm-surface)' }}>
      <Eyebrow>Related</Eyebrow>
      <div className="grid gap-4 md:grid-cols-3">
        {items.map((r) => (
          <Link key={r.href} to={r.href} className="rounded-2xl p-5 transition-colors" style={card}>
            <span className="block text-base font-medium" style={ink}>{r.title}</span>
            <span className="mt-1 block text-sm" style={muted}>{r.line}</span>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium" style={ink}>Read more <ArrowRight className="size-3.5" /></span>
          </Link>
        ))}
      </div>
    </RevealSection>
  );
}

function FinalCta({ c }: { c: V2Page['cta'] }) {
  return (
    <section>
      <div className="mx-auto max-w-[1200px] px-5 pb-16 sm:px-8 md:pb-24">
        <div className="grid items-center gap-8 rounded-3xl p-8 md:p-12 lg:grid-cols-2" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
          <div>
            <h2 className="font-display text-[2rem] font-light leading-tight md:text-[2.5rem]" style={ink}>{c.title}</h2>
            <p className="mt-3 text-base" style={muted}>{c.subtitle}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
              <Link to="/pricing" className="inline-flex min-h-[44px] items-center px-3 text-sm" style={muted}>See pricing</Link>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-1.5 text-sm" style={muted}>
              {['No credit card', 'Unlimited members', 'No contract'].map((f) => <li key={f} className="flex items-center gap-1.5"><IconCheck size={14} stroke={2.25} style={{ color: 'var(--warm-ok)' }} />{f}</li>)}
            </ul>
          </div>
          <Window title="New ticket" label="Example workspace">
            <div className="space-y-2.5">
              <div className="rounded-xl px-3 py-2.5 text-[13px]" style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)', ...muted }}>Describe the issue in your own words…</div>
              <div className="flex items-center justify-between gap-3">
                <span className="flex gap-1.5"><Chip>Medium</Chip><Chip>Other</Chip></span>
                <span className="rounded-lg px-3 py-1.5 text-xs font-medium" style={{ background: 'var(--warm-btn)', color: 'var(--warm-btn-text)' }}>Create ticket</span>
              </div>
              <p className="text-[11px]" style={muted}>$0.10 per ticket created. 1,100 free credits to start.</p>
            </div>
          </Window>
        </div>
      </div>
    </section>
  );
}

export function LandingV2Page({ page, preview = false }: { page: V2Page; preview?: boolean }) {
  useEffect(() => {
    setDocumentMeta(page.title, page.meta);
    setCanonical(page.url);
    return injectJsonLdList([
      buildWebPageSchema(page.url, page.title, page.meta),
      buildBreadcrumbSchema([{ name: 'Home', url: 'https://usekora.app' }, { name: page.h1, url: `https://usekora.app${page.url}` }]),
      buildFaqPageSchema(page.faq),
    ]);
  }, [page]);

  return (
    <MarketingShell>
      {preview && <p className="fixed bottom-3 left-3 z-30 rounded-md px-2.5 py-1 text-xs font-medium" style={{ background: 'var(--chip-amber-bg)', color: 'var(--chip-amber)' }}>Preview, not indexed</p>}
      <Hero p={page} />
      <div className="mx-auto max-w-3xl px-5 sm:px-8"><AnswerCapsule>{page.answer}</AnswerCapsule></div>
      <FeatureRows rows={page.rows} />
      <Steps steps={page.steps} />
      <Facts facts={page.facts} />
      <Module m={page.module} />
      <Cost c={page.cost} />
      <Setup s={page.setup} />
      <NotYet items={page.notYet} />
      <Faq faq={page.faq} />
      <Related items={page.related} />
      <FinalCta c={page.cta} />
    </MarketingShell>
  );
}

/** Hidden preview route: /preview/landing/<url with slashes as dashes>, e.g. /preview/landing/solutions-company-wide. */
export function LandingV2Route() {
  const { slug } = useParams();
  const page = V2_PAGES.find((p) => p.url.replace(/^\//, '').replace(/\//g, '-') === slug);
  if (!page) return <MarketingShell><p className="p-24 text-center">No such preview page.</p></MarketingShell>;
  return <LandingV2Page page={page} preview />;
}
